import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import AudiotrackIcon from '@material-ui/icons/Audiotrack';
import CloseIcon from '@material-ui/icons/Close';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';

import { i18n } from '../../translate/i18n';
import { getInternalChatLocalFilePreviewKind } from '../../utils/internalChatMedia';

const useModalStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    overflow: 'hidden',
    maxHeight: 'min(90vh, 640px)',
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
    boxShadow:
      theme.palette.type === 'dark' ? theme.shadows[24] : '0 8px 32px rgba(15, 23, 42, 0.14)',
    backgroundColor: theme.palette.background.paper,
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 2.25),
    borderBottom: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    '& svg': {
      fontSize: 22,
      color: theme.palette.primary.main,
    },
  },
  headerTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  headerSubtitle: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
    lineHeight: 1.35,
    wordBreak: 'break-all',
  },
  headerCloseBtn: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: alpha(theme.palette.text.primary, 0.06),
    },
  },
  dialogContent: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(2, 2, 2.5),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f8fafc',
  },
  previewCard: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e2e8f0'}`,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  },
  previewImage: {
    maxWidth: '100%',
    maxHeight: 'min(48vh, 360px)',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
    borderRadius: 8,
    display: 'block',
  },
  previewVideo: {
    maxWidth: '100%',
    maxHeight: 'min(48vh, 360px)',
    borderRadius: 8,
    backgroundColor: theme.palette.type === 'dark' ? '#000' : '#0f172a',
  },
  previewAudio: {
    width: '100%',
    maxWidth: 400,
  },
  filePlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    textAlign: 'center',
    padding: theme.spacing(2, 1),
    color: theme.palette.text.secondary,
  },
  filePlaceholderIcon: {
    fontSize: 48,
    opacity: 0.85,
    color: theme.palette.primary.main,
  },
  dialogActions: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  btn: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    minWidth: 100,
  },
}));

/**
 * Pré-visualização de anexo local antes do envio (chat interno / página Chat).
 * Estilo alinhado aos modais da página Chat (bordas 16px, fundo #f8fafc no claro).
 * "Enviar" dispara o envio; fechar/backdrop/remove descartam o anexo.
 */
export default function InternalChatAttachmentPreviewModal({
  open,
  file,
  onDiscard,
  onSend,
  sendDisabled = false,
}) {
  const classes = useModalStyles();
  const [objectUrl, setObjectUrl] = useState('');

  const kind = useMemo(() => (file ? getInternalChatLocalFilePreviewKind(file) : 'file'), [file]);

  useEffect(() => {
    if (!open || !file) {
      setObjectUrl('');
      return undefined;
    }
    const needsUrl = kind === 'image' || kind === 'video' || kind === 'audio';
    if (!needsUrl) {
      setObjectUrl('');
      return undefined;
    }
    const u = URL.createObjectURL(file);
    setObjectUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [open, file, kind]);

  const fileName = file?.name || '';

  const body = (() => {
    if (!file) return null;
    if (kind === 'image' && objectUrl) {
      return (
        <img
          src={objectUrl}
          alt={i18n.t('chatInternal.media.attachmentPreview.imageAlt')}
          className={classes.previewImage}
        />
      );
    }
    if (kind === 'video' && objectUrl) {
      return (
        <video
          src={objectUrl}
          className={classes.previewVideo}
          controls
          playsInline
          aria-label={i18n.t('chatInternal.media.attachmentPreview.videoLabel')}
        />
      );
    }
    if (kind === 'audio' && objectUrl) {
      return (
        <audio src={objectUrl} controls className={classes.previewAudio}>
          {i18n.t('chatInternal.media.attachmentPreview.audioHint')}
        </audio>
      );
    }
    if (kind === 'audio') {
      return (
        <Box className={classes.filePlaceholder}>
          <AudiotrackIcon className={classes.filePlaceholderIcon} />
          <Typography variant="body2">
            {i18n.t('chatInternal.media.attachmentPreview.audioHint')}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {fileName}
          </Typography>
        </Box>
      );
    }
    return (
      <Box className={classes.filePlaceholder}>
        <InsertDriveFileIcon className={classes.filePlaceholderIcon} />
        <Typography variant="body2">
          {i18n.t('chatInternal.media.attachmentPreview.fileHint')}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {fileName}
        </Typography>
      </Box>
    );
  })();

  return (
    <Dialog
      open={open && !!file}
      onClose={onDiscard}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      aria-labelledby="internal-chat-attachment-preview-title"
      PaperProps={{ className: classes.dialogPaper }}
      /** Acima do painel flutuante do widget (z-index 1300) e de drawers comuns. */
      ModalProps={{ style: { zIndex: 16000 } }}
    >
      <Box className={classes.dialogHeader} component="header">
        <Box className={classes.headerLeft}>
          <Box className={classes.headerIcon} aria-hidden>
            <AttachFileIcon />
          </Box>
          <Box minWidth={0}>
            <Typography
              component="h2"
              className={classes.headerTitle}
              id="internal-chat-attachment-preview-title"
            >
              {i18n.t('chatInternal.media.attachmentPreview.title')}
            </Typography>
            {fileName ? (
              <Typography
                className={classes.headerSubtitle}
                variant="body2"
                component="p"
                noWrap
                title={fileName}
              >
                {fileName}
              </Typography>
            ) : null}
          </Box>
        </Box>
        <IconButton
          className={classes.headerCloseBtn}
          onClick={onDiscard}
          aria-label={i18n.t('chatInternal.modal.cancel')}
          edge="end"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.previewCard}>{body}</Box>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button className={classes.btn} onClick={onDiscard} color="secondary">
          {i18n.t('chatInternal.media.attachmentPreview.remove')}
        </Button>
        <Button
          className={classes.btn}
          onClick={onSend}
          color="primary"
          variant="contained"
          disabled={sendDisabled}
        >
          {i18n.t('chatInternal.media.attachmentPreview.send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
